import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import { useThreadStore } from "@/store";
import PropTypes from "prop-types";
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { version as appVersion } from "../../../package.json";
import { ChatInput } from "./input/ChatInput";
import { MessageThreadSkeleton } from "./messages/components/MessageThreadSkeleton";
import { Messages } from "./messages/Messages";
import { ScrollToBottomButton } from "./ScrollToBottomButton";

// Memoized version display - never re-renders
const VersionDisplay = memo(function VersionDisplay() {
  return (
    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-[100] text-[10px] font-mono tracking-widest select-none pointer-events-none opacity-50 text-zinc-700 dark:text-zinc-400">
      v{appVersion}-{import.meta.env.VITE_GIT_HASH || "dev"}
    </div>
  );
});

/**
 * ChatContent - Production version with optimized streaming support
 * Simplified version with performance optimizations
 */
export function ChatContent({
  isLimitedMode = false,
  disableTools = false,
  disableFileUpload = false,
  onShowAuthOverlay,
}) {
  const {
    scrollContainerRef,
    bottomRef,
    messages,
    handleSendMessage,
    currentThreadId,
    scrollToBottom,
  } = useChat();

  const location = useLocation();
  const locationThreadId = useMemo(() => {
    const match = location.pathname.match(/\/chat\/([^/]+)/);
    return match?.[1] || null;
  }, [location.pathname]);
  const isNewChatRoute = location.pathname === "/chat/new";
  // "Effectively new chat" = on /chat/new route AND no messages loaded
  // This handles race conditions where messages load before route updates
  const isEffectivelyNewChat = isNewChatRoute && (!messages || messages.length === 0);
  const isLoading = useThreadStore((state) => state.isLoading);
  const inputContainerRef = useRef(null);
  const blankThreadTimerRef = useRef(null);
  const [showBlankThreadWindow, setShowBlankThreadWindow] = useState(false);
  const [blankTimerComplete, setBlankTimerComplete] = useState(false);
  const prevPathnameRef = useRef(location.pathname);
  const skipDetectionPathRef = useRef(location.pathname);
  const isLocationThreadPending =
    Boolean(locationThreadId) && locationThreadId !== currentThreadId;
  const [skipBlankAfterNewThread, setSkipBlankAfterNewThread] = useState(false);
  const previousLocationThreadIdRef = useRef(locationThreadId);

  // FAST THREAD SWITCHING: 100ms threshold before showing skeletons
  // This prevents flickering on fast loads while showing loading state for slower loads
  const [loadingThresholdMet, setLoadingThresholdMet] = useState(false);

  const shouldForceBlankRender =
    !isEffectivelyNewChat &&
    isLocationThreadPending;
  const shouldShowInputArea =
    !shouldForceBlankRender &&
    (!isLoading || loadingThresholdMet || isNewChatRoute) &&
    Boolean(currentThreadId || isNewChatRoute);

  useEffect(() => {
    if (isLoading) {
      // Start threshold timer when loading begins
      const timer = setTimeout(() => {
        setLoadingThresholdMet(true);
      }, 100);

      return () => {
        clearTimeout(timer);
        setLoadingThresholdMet(false);
      };
    } else {
      // Reset threshold when loading completes
      setLoadingThresholdMet(false);
    }
  }, [isLoading]);

  useLayoutEffect(() => {
    const previousPathname = skipDetectionPathRef.current;

    if (
      previousPathname === "/chat/new" &&
      location.pathname.startsWith("/chat/") &&
      location.pathname !== "/chat/new"
    ) {
      setSkipBlankAfterNewThread(true);
    }

    skipDetectionPathRef.current = location.pathname;
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (blankThreadTimerRef.current) {
      clearTimeout(blankThreadTimerRef.current);
      blankThreadTimerRef.current = null;
    }

    if (skipBlankAfterNewThread) {
      setShowBlankThreadWindow(false);
      setBlankTimerComplete(true);
      return undefined;
    }

    if (isNewChatRoute) {
      setShowBlankThreadWindow(false);
      setBlankTimerComplete(true);
      return undefined;
    }

    if (!currentThreadId || isLocationThreadPending) {
      setShowBlankThreadWindow(true);
      setBlankTimerComplete(false);
      return undefined;
    }

    setShowBlankThreadWindow(true);
    setBlankTimerComplete(false);

    blankThreadTimerRef.current = window.setTimeout(() => {
      setBlankTimerComplete(true);
      blankThreadTimerRef.current = null;
    }, 1);

    return () => {
      if (blankThreadTimerRef.current) {
        clearTimeout(blankThreadTimerRef.current);
        blankThreadTimerRef.current = null;
      }
    };
  }, [currentThreadId, isLocationThreadPending, isNewChatRoute, skipBlankAfterNewThread]);

  useEffect(() => {
    if (blankTimerComplete && !isLoading && !isLocationThreadPending) {
      setShowBlankThreadWindow(false);
    }
  }, [blankTimerComplete, isLoading, isLocationThreadPending]);

  useEffect(() => {
    const previousLocationThreadId = previousLocationThreadIdRef.current;

    if (isNewChatRoute) {
      setShowBlankThreadWindow(false);
      setBlankTimerComplete(true);
      setSkipBlankAfterNewThread(false);
      previousLocationThreadIdRef.current = locationThreadId;
      prevPathnameRef.current = location.pathname;
      return;
    }

    const previousPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;

    if (
      previousPathname === "/chat/new" &&
      currentPathname.startsWith("/chat/") &&
      currentPathname !== "/chat/new"
    ) {
      setSkipBlankAfterNewThread(true);
    }

    const shouldClearMessages =
      previousLocationThreadId &&
      locationThreadId &&
      locationThreadId !== previousLocationThreadId &&
      !isNewChatRoute &&
      !skipBlankAfterNewThread;

    if (shouldClearMessages) {
      setShowBlankThreadWindow(true);
      setBlankTimerComplete(false);
    }

    previousLocationThreadIdRef.current = locationThreadId;
    prevPathnameRef.current = location.pathname;
  }, [locationThreadId, location.pathname, isNewChatRoute, skipBlankAfterNewThread]);

  useEffect(() => {
    if (skipBlankAfterNewThread && !isNewChatRoute && messages?.length > 0) {
      setSkipBlankAfterNewThread(false);
    }
  }, [skipBlankAfterNewThread, isNewChatRoute, messages?.length]);

  useEffect(() => {
    if (showBlankThreadWindow || isLoading) return;

    if (scrollToBottom) {
      scrollToBottom();
    }
  }, [showBlankThreadWindow, isLoading, scrollToBottom, messages?.length]);

  // NOTE: Message clearing on /chat/new is now FULLY handled by ChatInterface
  // with navigation grace period to prevent clearing during navigation transitions.
  // This effect has been disabled to avoid conflicts with ChatInterface clearing logic.
  // Keeping this comment for historical context - do not re-enable clearing here!

  // Listen for stream completion
  useEffect(() => {
    const handleStreamComplete = () => {
      if (scrollToBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    };

    window.addEventListener("aiStreamComplete", handleStreamComplete);

    return () => {
      window.removeEventListener("aiStreamComplete", handleStreamComplete);
    };
  }, [scrollToBottom]);

  if (shouldForceBlankRender) {
    return (
      <div
        className="flex flex-col h-full w-full overflow-hidden bg-background"
        data-thread-id="pending"
        data-message-count={0}
        data-chat-content="true"
      >
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto overflow-x-hidden pb-0"
        >
          <MessageThreadSkeleton />
          <div ref={bottomRef} className="h-4" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col h-full w-full overflow-hidden"
      data-thread-id={currentThreadId || "new"}
      data-message-count={messages?.length || 0}
      data-chat-content="true"
    >
      {/* Messages Area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden pb-24"
      >
        {/* FAST THREAD SWITCHING: Show skeletons if loading and threshold met, otherwise show messages */}
        {isLoading &&
        loadingThresholdMet &&
        (!messages || messages.length === 0) ? (
          <MessageThreadSkeleton />
        ) : (
          <div className="flex-1 flex justify-center w-full">
            <div className="w-full max-w-4xl px-4 sm:px-6 flex-grow">
              <Messages
                messages={messages || []}
                id="messages"
                className="w-full flex-grow"
              />
            </div>
          </div>
        )}
        <div ref={bottomRef} className="h-4" aria-hidden="true" />
      </div>

      {/* Input Area - Hide until thread has loaded */}
      {shouldShowInputArea &&
        !(
          isLoading &&
          loadingThresholdMet &&
          (!messages || messages.length === 0)
        ) && (
          <div
            className={cn(
              "flex-shrink-0 w-full z-[60] flex justify-center relative bg-background",
              isEffectivelyNewChat ? "flex items-center" : "min-h-24 pb-6"
            )}
          >
            <div
              ref={inputContainerRef}
              className={cn(
                "w-full max-w-4xl mx-auto px-4 sm:px-6 flex-grow bg-transparent relative",
                isEffectivelyNewChat && "transform",
                !isEffectivelyNewChat && "z-[60] -mt-6"
              )}
              style={{
                transform: isEffectivelyNewChat ? "translateY(-21.5rem)" : undefined,
              }}
            >
              {/* Scroll to bottom button - only show when not effectively new chat */}
              {!isEffectivelyNewChat && (
                <ScrollToBottomButton
                  containerRef={scrollContainerRef}
                  scrollToBottom={scrollToBottom}
                />
              )}

              <ChatInput
                onSendMessage={handleSendMessage}
                isNewChat={location.pathname === "/chat/new"}
                isLimitedMode={isLimitedMode}
                disableTools={disableTools}
                disableFileUpload={disableFileUpload}
                onShowAuthOverlay={onShowAuthOverlay}
              />
            </div>
          </div>
        )}

      <VersionDisplay />
    </div>
  );
}

ChatContent.propTypes = {
  isLimitedMode: PropTypes.bool,
  disableTools: PropTypes.bool,
  disableFileUpload: PropTypes.bool,
  onShowAuthOverlay: PropTypes.func,
};

export default ChatContent;
